import type React from 'react';
import { useId } from 'react';

export type LexiMood = 'happy' | 'excited' | 'focused' | 'surprised' | 'sleepy';

export interface LexiProps {
  mood?: LexiMood;
  size?: number;
  'aria-label'?: string;
  className?: string;
}

const SW = 2.5;

function Eyes({ mood }: { mood: LexiMood }) {
  switch (mood) {
    case 'excited':
      return (
        <>
          <path d="M 17,29 Q 22,21 27,29" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />
          <path d="M 37,29 Q 42,21 47,29" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />
        </>
      );
    case 'focused':
      return (
        <>
          <path d="M 17,27 Q 22,25 27,27" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />
          <path d="M 37,27 Q 42,25 47,27" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />
        </>
      );
    case 'surprised':
      return (
        <>
          <circle cx={22} cy={26} r={5.5} fill="var(--wh-ink)" />
          <circle cx={42} cy={26} r={5.5} fill="var(--wh-ink)" />
        </>
      );
    case 'sleepy':
      // Bottom-half arc creates a droopy half-closed eye silhouette
      return (
        <>
          <path d="M 17,27 A 5,4 0 0 1 27,27 Z" fill="var(--wh-ink)" />
          <path d="M 37,27 A 5,4 0 0 1 47,27 Z" fill="var(--wh-ink)" />
        </>
      );
    default:
      return (
        <>
          <ellipse cx={22} cy={27} rx={3.5} ry={4} fill="var(--wh-ink)" />
          <ellipse cx={42} cy={27} rx={3.5} ry={4} fill="var(--wh-ink)" />
        </>
      );
  }
}

function Mouth({ mood }: { mood: LexiMood }) {
  switch (mood) {
    case 'excited':
      return <ellipse cx={32} cy={42} rx={7} ry={5} fill="var(--wh-ink)" />;
    case 'focused':
      return <path d="M 24,41 L 40,41" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />;
    case 'surprised':
      return <circle cx={32} cy={43} r={5} fill="var(--wh-ink)" />;
    case 'sleepy':
      return (
        <path d="M 26,42 Q 32,39 38,42" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />
      );
    default:
      return (
        <path d="M 22,40 Q 32,48 42,40" stroke="var(--wh-ink)" strokeWidth={SW} fill="none" strokeLinecap="round" />
      );
  }
}

export const Lexi: React.FC<LexiProps> = ({ mood = 'happy', size = 64, 'aria-label': ariaLabel, className }) => {
  const titleId = useId();
  const label = ariaLabel ?? `Lexi, looking ${mood}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-labelledby={titleId}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{label}</title>
      <ellipse cx={32} cy={36} rx={24} ry={22} fill="var(--wh-teal)" stroke="var(--wh-ink)" strokeWidth={SW} />
      <Eyes mood={mood} />
      <Mouth mood={mood} />
    </svg>
  );
};
